import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../../../utils/date";
import { formatNumber } from "../../../utils";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import {
  DeductionDay,
  DeductionRouteDetail,
} from "../../../types/deduction.type";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RoleChip from "../RoleChip";
import SaleDetailModal from "./SaleDetailModal";

const compact = "@media " + COMPACT_QUERY;

interface RouteDetailProps {
  route: DeductionRouteDetail;
}

export default function RouteDetail({ route }: RouteDetailProps) {
  const navigate = useNavigate();
  const [openDays, setOpenDays] = useState<Record<string, boolean>>({});
  const [saleDay, setSaleDay] = useState<DeductionDay | null>(null);

  const handleToggle = (date: string) => {
    setOpenDays((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  const monthLabel = formatDate(`${route.year_month}-01`, "MMMM-YYYY");

  const openStaffSummary = (staffUuid: string, fullname: string) => {
    const params = new URLSearchParams({
      year_month: route.year_month,
      name: fullname,
    });
    navigate(`/report/deduction/staff/${staffUuid}?${params.toString()}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          [compact]: { flexWrap: "wrap", gap: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Link
            to="/report/deduction"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <ArrowBackIosNewIcon />
          </Link>
          <Box sx={{ ml: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                sx={{
                  bgcolor: (theme) => theme.palette.primary.main + "1A",
                  cursor: "default",
                  "&:hover, &:focus": {
                    bgcolor: (theme) => theme.palette.primary.main + "1A",
                  },
                }}
              >
                <AirportShuttleOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: (theme) => theme.palette.primary.main,
                  }}
                />
              </IconButton>
              <Typography variant="h6" fontWeight={500}>
                {route.route_code}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {route.branch_name} · {monthLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography variant="body2" color="text.secondary">
            ยอดหักรวมทั้งเดือน
          </Typography>
          <Typography color="error" variant="h6" fontWeight={600}>
            -{formatNumber(route.total_amount)} ฿
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {route.day_count} วันที่มีการหัก
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        รายละเอียดรายวัน — ยอดหักถูกเฉลี่ยเท่ากันแก่พนักงานที่ออกสายในวันนั้น
      </Typography>

      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                วันที่ / พนักงาน
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ตำแหน่ง
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ยอดหัก
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {route.days.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {route.days.map((day, index) => {
              const open = !!openDays[day.date];
              return (
                <Fragment key={day.sale_report_uuid || day.date}>
                  <TableRow
                    hover
                    sx={{
                      bgcolor: open
                        ? "#f3f3f6"
                        : index % 2 === 1
                          ? "#f6f6f9"
                          : "#fff",
                      cursor: "pointer",
                      "& td, & th": { padding: "8px" },
                    }}
                    onClick={() => handleToggle(day.date)}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton size="small">
                          {open ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(day.date)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            รวม {day.emp_count} คน
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      <Typography color="error" fontWeight={500}>
                        -{formatNumber(day.total_amount)} ฿
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="info"
                        disabled={!day.sale_report_uuid}
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSaleDay(day);
                        }}
                      >
                        รายงานการขายแบบละเอียด
                      </Button>
                    </TableCell>
                  </TableRow>

                  {open &&
                    day.employees.map((emp) => (
                      <TableRow
                        key={`${day.date}-${emp.staff_uuid}`}
                        sx={{
                          bgcolor: "#fafafa",
                          "& td": { border: 0, py: 0.5 },
                        }}
                      >
                        <TableCell sx={{ pl: 7 }}>
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ cursor: "pointer", fontWeight: 500 }}
                            onClick={() =>
                              openStaffSummary(emp.staff_uuid, emp.fullname)
                            }
                          >
                            {emp.fullname}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <RoleChip role={emp.role} />
                        </TableCell>
                        <TableCell>
                          <Typography color="error">
                            -{formatNumber(emp.share_amount)} ฿
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <SaleDetailModal
        open={!!saleDay}
        onClose={() => setSaleDay(null)}
        day={saleDay}
        route={route}
      />
    </Paper>
  );
}
